/**
 * Conector directo Web Bluetooth para impresoras térmicas ESC/POS portátiles e inalámbricas.
 * Compatible con Android, Windows, Mac y ChromeOS en navegadores basados en Chromium.
 */

export interface BluetoothPrinterInfo {
  name: string;
  id: string;
}

// UUIDs habituales de servicios de impresión térmica BLE
const COMMON_PRINTER_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000af30-0000-1000-8000-00805f9b34fb",
];

export class WebBluetoothPrinter {
  private device: any = null;
  private characteristic: any = null;

  public static isSupported(): boolean {
    return typeof navigator !== "undefined" && "bluetooth" in navigator;
  }

  public getInfo(): BluetoothPrinterInfo | null {
    if (!this.device) return null;
    return {
      name: this.device.name || "Impresora Bluetooth",
      id: this.device.id,
    };
  }

  public isConnected(): boolean {
    return this.device !== null && this.device.gatt?.connected && this.characteristic !== null;
  }

  /**
   * Abre el diálogo de emparejamiento Bluetooth nativo del navegador.
   */
  public async requestAndConnect(): Promise<BluetoothPrinterInfo> {
    if (!WebBluetoothPrinter.isSupported()) {
      throw new Error("Web Bluetooth no es compatible con este navegador.");
    }

    try {
      const bluetooth = (navigator as any).bluetooth;
      const device = await bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: COMMON_PRINTER_SERVICES,
      });

      await this.connectDevice(device);
      const info = this.getInfo();
      if (!info) throw new Error("No se pudo obtener información de la impresora Bluetooth");
      return info;
    } catch (err) {
      this.device = null;
      this.characteristic = null;
      throw err;
    }
  }

  private async connectDevice(device: any): Promise<void> {
    this.device = device;
    const server = await device.gatt.connect();

    // Buscar el servicio y característica de escritura
    let targetChar: any = null;

    // Intentar buscar entre los servicios conocidos
    for (const serviceUuid of COMMON_PRINTER_SERVICES) {
      try {
        const service = await server.getPrimaryService(serviceUuid);
        const characteristics = await service.getCharacteristics();
        for (const char of characteristics) {
          if (char.properties.write || char.properties.writeWithoutResponse) {
            targetChar = char;
            break;
          }
        }
        if (targetChar) break;
      } catch {
        // Continuar buscando en el siguiente servicio
      }
    }

    // Si no se encontró en la lista conocida, buscar en todos los servicios primarios expuestos
    if (!targetChar) {
      try {
        const services = await server.getPrimaryServices();
        for (const service of services) {
          const characteristics = await service.getCharacteristics();
          for (const char of characteristics) {
            if (char.properties.write || char.properties.writeWithoutResponse) {
              targetChar = char;
              break;
            }
          }
          if (targetChar) break;
        }
      } catch {
        // Fallback
      }
    }

    if (!targetChar) {
      throw new Error("No se encontró canal de escritura (Write Characteristic) en la impresora Bluetooth.");
    }

    this.characteristic = targetChar;
  }

  /**
   * Envía datos en fragmentos de 50 bytes (adecuado para BLE MTU).
   */
  public async send(data: Uint8Array): Promise<void> {
    if (!this.isConnected() || !this.characteristic) {
      throw new Error("La impresora Bluetooth no está conectada");
    }

    const CHUNK_SIZE = 64;
    for (let offset = 0; offset < data.length; offset += CHUNK_SIZE) {
      const chunk = data.slice(offset, offset + CHUNK_SIZE);
      if (this.characteristic.writeValueWithoutResponse) {
        await this.characteristic.writeValueWithoutResponse(chunk);
      } else {
        await this.characteristic.writeValue(chunk);
      }
      // Pequeño retardo de 10ms entre paquetes BLE para evitar desbordamiento del buffer
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
  }

  public async disconnect(): Promise<void> {
    if (this.device?.gatt?.connected) {
      try {
        this.device.gatt.disconnect();
      } catch {
        // Ignorar errores al desconectar
      }
    }
    this.device = null;
    this.characteristic = null;
  }
}
